import { createSelector, createSlice } from "@reduxjs/toolkit";
import { artTypes } from "../configs";
import { findArtifact, findCharacter, findWeapon } from "../data/controllers";
import {
  ascsFromLv,
  findById,
  findByName,
  indexById,
  indexByName,
  splitLv
} from "../helpers";
import { processNumInp } from "../helpers/processors";
import { initCharInfo, initWeapon } from "./calculatorSlice/initiators";

export const databaseSlice = createSlice({
  name: "database",
  initialState: {
    myChars: [],
    myWps: [],
    myArts: [],
    chosenChar: ""
  },
  reducers: {
    UPLOAD_DB: (state, action) => {
      const { Characters, Weapons, Artifacts } = action.payload;
      state.myChars = Characters;
      state.myWps = Weapons;
      state.myArts = Artifacts;
      state.chosenChar = Characters.length ? Characters[0].name : "";
    },
    CHOOSE_MY_CHAR: (state, action) => {
      state.chosenChar = action.payload;
    },
    ADD_CHAR: (state, action) => {
      const { name, weapon } = action.payload;
      const weaponID = Date.now();
      state.chosenChar = name;
      state.myChars.unshift({
        name,
        ...initCharInfo({}),
        weaponID,
        artIDs: [null, null, null, null, null]
      });
      state.myWps.unshift({
        ID: weaponID,
        user: name,
        ...initWeapon({ type: weapon })
      });
    },
    SORT_CHARS: (state, action) => {
      state.myChars = action.payload.map((index) => state.myChars[index]);
    },
    TOGGLE_CONS: (state, action) => {
      const { name, consIndex } = action.payload;
      const char = findByName(state.myChars, name);
      char.constellation =
        char.constellation === consIndex + 1 ? consIndex : consIndex + 1;
    },
    CHANGE_TALENT: (state, action) => {
      const { name, type, level } = action.payload;
      findByName(state.myChars, name)[type] = level;
    },
    LEVEL_CHAR: (state, action) => {
      const { name, level } = action.payload;
      const char = findByName(state.myChars, name);
      char.level = level;
      char.ascension = ascsFromLv(level);
    },
    REMOVE_CHAR: (state, action) => {
      const { myChars, myWps, myArts } = state;
      const name = action.payload;
      const { weaponID, artIDs } = findByName(myChars, name);
      findById(myWps, weaponID).user = null;
      for (const id of artIDs) {
        if (id) findById(myArts, id).user = null;
      }
      let charI = indexByName(myChars, name);
      myChars.splice(charI, 1);
      if (charI === myChars.length) charI--;
      state.chosenChar = myChars[charI]?.name || "";
    },
    SWITCH_WP: ({ myWps, myChars }, action) => {
      const { newUser, newID, oldUser, oldID } = action.payload;
      findById(myWps, newID).user = oldUser;
      findById(myWps, oldID).user = newUser;
      findByName(myChars, oldUser).weaponID = newID;
      if (newUser) {
        findByName(myChars, newUser).weaponID = oldID;
      }
    },
    SWITCH_ART: ({ myArts, myChars }, action) => {
      const { newUser, newID, oldUser, oldID, pos } = action.payload;
      findById(myArts, newID).user = oldUser;
      if (oldID) {
        findById(myArts, oldID).user = newUser;
      }
      findByName(myChars, oldUser).artIDs[pos] = newID;
      if (newUser) {
        findByName(myChars, newUser).artIDs[pos] = oldID;
      }
    },
    UNEQUIP_ART: (state, action) => {
      const { user, artID, pos } = action.payload;
      findByName(state.myChars, user).artIDs[pos] = null;
      findById(state.myArts, artID).user = null;
    },
    // wp
    ADD_WP: (state, action) => {
      const { ID, item } = action.payload;
      state.myWps.unshift({ ID, user: null, ...item });
    },
    SORT_WPS: (state) => {
      state.myWps.sort((a, b) => {
        const rA = findWeapon(a).rarity;
        const rB = findWeapon(b).rarity;
        if (rA !== rB) return rB - rA;
        const [fA, sA] = splitLv(a);
        const [fB, sB] = splitLv(b);
        if (fA !== fB) {
          return fB - fA;
        }
        if (a.type !== b.type) {
          const type = {
            Bow: 5,
            Catalyst: 4,
            Polearm: 3,
            Claymore: 2,
            Sword: 1
          };
          return type[b.type] - type[a.type];
        }
        return sB - sA;
      });
    },
    SWAP_WP_USER: ({ myChars, myWps }, action) => {
      const { newUser, wpID, oldUser } = action.payload;
      findById(myWps, wpID).user = newUser;
      const newChar = findByName(myChars, newUser);
      const { weaponID } = newChar;
      findById(myWps, weaponID).user = oldUser;
      if (oldUser) {
        findByName(myChars, oldUser).weaponID = weaponID;
      }
      newChar.weaponID = wpID;
    },
    REMOVE_WP: ({ myWps, myChars }, action) => {
      const { ID, user, type } = action.payload;
      myWps.splice(indexById(myWps, ID), 1);
      if (user) {
        const newWpID = Date.now();
        myWps.unshift({
          ID: newWpID,
          user,
          ...initWeapon({ type })
        });
        findByName(myChars, user).weaponID = newWpID;
      }
    },
    UPGRADE_WP: (state, action) => {
      const { ID, level } = action.payload;
      findById(state.myWps, ID).level = level;
    },
    REFINE_WP: (state, action) => {
      const { ID, rfm } = action.payload;
      findById(state.myWps, ID).refinement = rfm;
    },
    // art
    ADD_ART: (state, action) => {
      state.myArts.unshift(action.payload);
    },
    SORT_ARTS: (state) => {
      state.myArts.sort((a, b) => {
        if (a.level !== b.level) {
          return b.level - a.level;
        }
        if (a.type !== b.type) {
          const type = {
            flower: 5,
            plume: 4,
            sands: 3,
            goblet: 2,
            circlet: 1
          };
          return type[b.type] - type[a.type];
        }
        const aName = findArtifact({ code: a.code }).name;
        const bName = findArtifact({ code: b.code }).name;
        return bName.localeCompare(aName);
      });
    },
    SWAP_ART_USER: ({ myChars, myArts }, action) => {
      const { newUser, artID, artType, oldUser } = action.payload;
      findById(myArts, artID).user = newUser;
      const { artIDs } = findByName(myChars, newUser);
      const pos = artTypes.indexOf(artType);
      if (artIDs[pos]) {
        findById(myArts, artIDs[pos]).user = oldUser;
      }
      if (oldUser) {
        findByName(myChars, oldUser).artIDs[pos] = artIDs[pos];
      }
      artIDs[pos] = artID;
    },
    REMOVE_ART: ({ myArts, myChars }, action) => {
      const { ID, user, type } = action.payload;
      myArts.splice(indexById(myArts, ID), 1);
      if (user) {
        const pos = artTypes.indexOf(type);
        findByName(myChars, user).artIDs[pos] = null;
      }
    },
    ENHANCE_ART: (state, action) => {
      const { ID, level } = action.payload;
      findById(state.myArts, ID).level = level;
    },
    ENHANCE_ART_BY_4: (state, action) => {
      const { ID, rarity } = action.payload;
      const art = findById(state.myArts, ID);
      art.level = Math.min(art.level + 4, rarity === 4 ? 16 : 20);
    },
    ENHANCE_ART_TO_MAX: (state, action) => {
      const { ID, rarity } = action.payload;
      findById(state.myArts, ID).level = rarity === 4 ? 16 : 20;
    },
    CHANGE_ART_MS_TYPE: (state, action) => {
      const { ID, type } = action.payload;
      findById(state.myArts, ID).mainSType = type;
    },
    CHANGE_ART_SS_TYPE: (state, action) => {
      const { ID, ssIndex, type } = action.payload;
      findById(state.myArts, ID).subS[ssIndex].type = type;
    },
    CHANGE_ART_SS_VAL: (state, action) => {
      const { ID, ssIndex, value } = action.payload;
      const subS = findById(state.myArts, ID).subS[ssIndex];
      subS.val = processNumInp(value, subS.val, 9999);
    },
    OVERWRITE_ART: ({ myArts }, action) => {
      const { ID, ...stats } = action.payload;
      const index = indexById(myArts, ID);
      myArts[index] = { ...myArts[index], ...stats };
    },
    CALCULATOR_UPDATE_DB: (state, action) => {
      const { myChars, myWps, myArts } = state;
      const [char, wp, arts, actionIs] = action.payload;
      const charI = indexByName(myChars, char.name);
      if (actionIs[1]) {
        const wpI = indexById(myWps, wp.ID);
        const wpInfo = { ...wp, user: char.name };
        if (wpI === -1) {
          myWps.unshift(wpInfo);
        } else {
          const oldUser = myWps[wpI].user;
          myWps[wpI] = wpInfo;
          if (oldUser && oldUser !== char.name) {
            let ID;
            if (charI === -1) {
              ID = Date.now();
              myWps.unshift({
                ID,
                user: oldUser,
                ...initWeapon({ type: findCharacter({ name: oldUser }).weapon })
              });
            } else {
              ID = myChars[charI].weaponID;
              findById(myWps, ID).user = oldUser;
            }
            findByName(myChars, oldUser).weaponID = ID;
          }
        }
      }
      arts.forEach((p, i) => {
        if (actionIs[2][i] && p) {
          const pI = indexById(myArts, p.ID);
          const artInfo = { ...p, user: char.name };
          if (pI === -1) {
            myArts.push(artInfo);
          } else {
            const oldUser = myArts[pI].user;
            myArts[pI] = artInfo;
            if (oldUser && oldUser !== char.name) {
              let ID = null;
              if (charI !== -1) {
                ID = myChars[charI].artIDs[i];
                if (ID) findById(myArts, ID).user = oldUser;
              }
              findByName(myChars, oldUser).artIDs[i] = ID;
            }
          }
        }
      });
      const charInfo = {
        ...char,
        weaponID: wp.ID,
        artIDs: arts.map((p) => (p ? p.ID : null))
      };
      if (charI === -1) {
        myChars.unshift(charInfo);
      } else {
        const { weaponID, artIDs } = myChars[charI];
        if (weaponID && weaponID !== charInfo.weaponID) {
          findById(myWps, weaponID).user = null;
        }
        artIDs.forEach((id) => {
          if (id && !charInfo.artIDs.includes(id)) {
            findById(myArts, id).user = null;
          }
        });
        myChars[charI] = charInfo;
      }
      state.chosenChar = char.name;
    }
  }
});

export const {
  UPLOAD_DB,
  CHOOSE_MY_CHAR,
  ADD_CHAR,
  SORT_CHARS,
  TOGGLE_CONS,
  CHANGE_TALENT,
  LEVEL_CHAR,
  REMOVE_CHAR,
  SWITCH_WP,
  SWITCH_ART,
  UNEQUIP_ART,
  ADD_WP,
  SORT_WPS,
  SWAP_WP_USER,
  REMOVE_WP,
  UPGRADE_WP,
  REFINE_WP,
  ADD_ART,
  SORT_ARTS,
  SWAP_ART_USER,
  REMOVE_ART,
  ENHANCE_ART,
  ENHANCE_ART_BY_4,
  ENHANCE_ART_TO_MAX,
  CHANGE_ART_MS_TYPE,
  CHANGE_ART_SS_TYPE,
  CHANGE_ART_SS_VAL,
  OVERWRITE_ART,
  CALCULATOR_UPDATE_DB
} = databaseSlice.actions;

export const selectMyChars = (state) => state.database.myChars;
export const selectMyWps = (state) => state.database.myWps;
export const selectMyArts = (state) => state.database.myArts;

export const selFilteredWpIds = createSelector(
  selectMyWps,
  (_, types) => types,
  (myWps, types) => {
    const result = types.length
      ? myWps.filter((wp) => types.includes(wp.type))
      : myWps;
    return result.map(({ ID }) => ID);
  }
);

export const selWpById = createSelector(
  selectMyWps,
  (_, ID) => ID,
  (myWps, ID) => findById(myWps, ID)
);

export default databaseSlice.reducer;
